import React, { useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import ReportingContainer from "features/Reporting/containers/Reporting.container"

import { Loader } from "shared/ui/Loader/Loader.component"
import { notify } from "shared/ui/Notifications/notify"

import {
  useProjectDetailsQuery,
  ProjectNotFoundError,
} from "features/Projects/api/useProjectsQuery"
import { useUserStore } from "stores/userStore"
import { useProjectStore } from "stores/projectStore"

const Reporting: React.FC = () => {
  const project = useProjectStore((state) => state.project)
  const setProject = useProjectStore((state) => state.setProject)
  const navigate = useNavigate()
  const user = useUserStore((state) => state.user)

  // Extracts the project ID from the URL parameters
  const { id } = useParams<{ id: string }>()

  const userReady = Boolean(user && user.username && user.unit)
  const detailsQuery = useProjectDetailsQuery(id, userReady)

  useEffect(() => {
    if (!userReady) {
      navigate("/home")
    } else if (!id) {
      navigate("/home")
      notify.error("Invalid Project", "No project ID provided in the URL.")
    }
  }, [userReady, id, navigate])

  // Sync fetched details into the project store used across the app.
  useEffect(() => {
    if (detailsQuery.data) {
      setProject({
        id: detailsQuery.data.id,
        Owner: detailsQuery.data.owner,
        ...detailsQuery.data.metadata,
      })
    }
  }, [detailsQuery.data, setProject])

  // Any load failure returns home — transport errors have already been
  // notified via the query's errorTitle meta.
  useEffect(() => {
    if (!detailsQuery.error) return
    navigate("/home")
    if (detailsQuery.error instanceof ProjectNotFoundError) {
      notify.error(
        "Project Not Found",
        "The project you are looking for does not exist."
      )
    }
  }, [detailsQuery.error, navigate])

  // Hold the loader until the store has the fetched project: consumers
  // capture the store on first mount, and the setProject sync effect runs
  // one tick after data arrives.
  const isStoreSynced =
    detailsQuery.data !== undefined && project.id === detailsQuery.data.id

  if (detailsQuery.isFetching || !isStoreSynced) {
    return (
      <div className="absolute left-2/4 top-2/4">
        <Loader fontSize={48} color="#36C3ED" />
      </div>
    )
  }

  return (
    <>
      <title>{project.Name}</title>
      <ReportingContainer />
    </>
  )
}

export default Reporting
